import type { PlaybookCriterionNode } from "@repo/app-types/entities/playbook";
import type {
  CriterionSampleGradingsPayload,
  SectionSampleGradingsPayload,
} from "@repo/app-types/entities/playbookProposalDetail";
import type { GradingId } from "@repo/app-types/entities/teammatePerformanceSummary";
import { Size } from "@repo/ui/helpers/helpers";
import { Tab } from "@repo/ui/Link/routerLink";
import { SlideoverContainer } from "@repo/ui/Slideover/slideoverContainer";
import {
  SlideoverDialog,
  SlideoverDialogContent,
  SlideoverDialogTitleHeader,
} from "@repo/ui/Slideover/slideoverDialog";
import { useState, type FC, type PropsWithChildren } from "react";
import { TabList, TabPanel, Tabs } from "react-aria-components";
import { GradingValidationDialogContainer } from "../../features/teammatePerformance/gradingValidationDialogContainer.js";
import { TableCard } from "../layout/pageLayout.js";
import { FeedbackGradingsTable } from "./feedbackGradingsTable.js";
import { SampleGradingsTable } from "./sampleGradingsTable.js";
import { SectionFeedbackGradingsTable } from "./sectionFeedbackGradingsTable.js";
import { SectionSampleGradingsTable } from "./sectionSampleGradingsTable.js";

export type GradedCallsTab = "sample" | "feedback";

const tabClassName =
  "flex min-h-8 shrink-0 cursor-pointer items-center border-b-2 border-transparent px-1 pt-px text-xs leading-5 whitespace-nowrap text-muted-foreground transition-colors outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset selected:border-foreground selected:text-foreground";

interface CriterionGradedCallsProps {
  nodeType: "criterion";
  gradingValueType: PlaybookCriterionNode["grading_value_type"];
  gradings: CriterionSampleGradingsPayload | undefined;
}

interface SectionGradedCallsProps {
  nodeType: "section";
  gradings: SectionSampleGradingsPayload | undefined;
}

type GradedCallsNodeProps = CriterionGradedCallsProps | SectionGradedCallsProps;

interface CommonGradedCallsSlideoverProps {
  isOpen: boolean;
  onClose: () => void;
  /** Tab shown when the slideover opens; the user can switch from there. */
  initialTab: GradedCallsTab;
  isLoading: boolean;
  isError: boolean;
}

type GradedCallsSlideoverProps = CommonGradedCallsSlideoverProps &
  GradedCallsNodeProps;

/**
 * Slideover listing the calls a proposal was regraded against. The tab and
 * call selection live in {@link GradedCallsSlideoverContent}, which the
 * overlay mounts only while open, so both reset on every reopen.
 */
export const GradedCallsSlideover: FC<GradedCallsSlideoverProps> = (props) => {
  const { isOpen, onClose, ...contentProps } = props;

  return (
    <SlideoverContainer
      isOpen={isOpen}
      onOpenChange={(newIsOpen) => {
        if (!newIsOpen) {
          onClose();
        }
      }}
      isDismissable
      size={Size.LARGE}
    >
      <SlideoverDialog label="Graded calls">
        <GradedCallsSlideoverContent {...contentProps} />
      </SlideoverDialog>
    </SlideoverContainer>
  );
};

interface CommonGradedCallsSlideoverContentProps {
  initialTab: GradedCallsTab;
  isLoading: boolean;
  isError: boolean;
}

type GradedCallsSlideoverContentProps = CommonGradedCallsSlideoverContentProps &
  GradedCallsNodeProps;

/** The graded call opened in the nested call slideover. */
interface SelectedCall {
  gradingId: GradingId;
  /** Node the call dialog focuses; the dialog can move it to a sibling criterion. */
  nodeId: string;
}

const GradedCallsSlideoverContent: FC<GradedCallsSlideoverContentProps> = (
  props,
) => {
  const { initialTab, isLoading, isError, ...nodeProps } = props;
  const [selectedCall, setSelectedCall] = useState<SelectedCall | null>(null);

  const handleGradingSelect = (gradingId: GradingId): void => {
    const nodeId = nodeProps.gradings?.playbook_node_id;
    if (nodeId === undefined) return;

    setSelectedCall({
      gradingId,
      nodeId,
    });
  };

  const handleCriteriaSelect = (criteriaId: string): void => {
    setSelectedCall((call) => (call ? { ...call, nodeId: criteriaId } : null));
  };

  const nodeNoun = nodeProps.nodeType === "criterion" ? "criteria" : "section";

  return (
    <>
      <SlideoverDialogTitleHeader>Graded calls</SlideoverDialogTitleHeader>
      <SlideoverDialogContent>
        <Tabs
          defaultSelectedKey={initialTab}
          className="flex min-h-0 flex-1 flex-col"
        >
          <TabList
            aria-label="Graded call groups"
            className="flex min-h-8 items-center gap-4 border-b border-border-light px-4 pt-2"
          >
            <Tab id="sample" className={tabClassName}>
              Sample calls
            </Tab>
            <Tab id="feedback" className={tabClassName}>
              Feedback calls
            </Tab>
          </TabList>

          <GradedCallsTabPanel
            id="sample"
            description={`Calls automatically selected and regraded to test this ${nodeNoun} update against the current baseline.`}
          >
            {nodeProps.nodeType === "criterion" ? (
              <SampleGradingsTable
                gradingValueType={nodeProps.gradingValueType}
                entries={nodeProps.gradings?.sample_grades ?? []}
                isLoading={isLoading}
                isError={isError}
                onGradingSelect={handleGradingSelect}
              />
            ) : (
              <SectionSampleGradingsTable
                entries={nodeProps.gradings?.sample_grades ?? []}
                isLoading={isLoading}
                isError={isError}
                onGradingSelect={handleGradingSelect}
              />
            )}
          </GradedCallsTabPanel>

          <GradedCallsTabPanel
            id="feedback"
            description="Calls regraded against grading feedback left by your teammates."
          >
            {nodeProps.nodeType === "criterion" ? (
              <FeedbackGradingsTable
                gradingValueType={nodeProps.gradingValueType}
                entries={nodeProps.gradings?.feedback_grades ?? []}
                isLoading={isLoading}
                isError={isError}
                onGradingSelect={handleGradingSelect}
              />
            ) : (
              <SectionFeedbackGradingsTable
                entries={nodeProps.gradings?.feedback_grades ?? []}
                isLoading={isLoading}
                isError={isError}
                onGradingSelect={handleGradingSelect}
              />
            )}
          </GradedCallsTabPanel>
        </Tabs>
      </SlideoverDialogContent>

      <SlideoverContainer
        isOpen={selectedCall !== null}
        onOpenChange={(newIsOpen) => {
          if (!newIsOpen) {
            setSelectedCall(null);
          }
        }}
        isDismissable
      >
        {selectedCall ? (
          <GradingValidationDialogContainer
            gradingId={selectedCall.gradingId}
            sectionOrCriteriaId={selectedCall.nodeId}
            onCriteriaSelect={handleCriteriaSelect}
          />
        ) : null}
      </SlideoverContainer>
    </>
  );
};

interface GradedCallsTabPanelProps extends PropsWithChildren {
  id: GradedCallsTab;
  description: string;
}

/** A tab's body: its explanatory copy above the table card. Inactive panels stay unmounted. */
const GradedCallsTabPanel: FC<GradedCallsTabPanelProps> = (props) => {
  const { id, description, children } = props;

  return (
    <TabPanel
      id={id}
      className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden px-4 py-4 outline-none"
    >
      <p className="text-sm text-muted-foreground">{description}</p>
      <TableCard>{children}</TableCard>
    </TabPanel>
  );
};
